import {
  AttachmentFileBase,
  Conversation,
  Id,
  MediaFile,
  ConversationsResponse as Response,
  convertConversation,
} from '@nextforce/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import { getRecentMessagesFromCache } from 'chat/messages/queries/shared/utils'
import { appendFileToFormData } from 'chat/shared/util'
import { useChatWebsocketContext } from 'chat/websocket/context'
import { updateConversationsCache, updateDetailsCache } from './shared/utils'

type Variables = {
  changes?: Partial<Conversation>
  icon?: MediaFile
}

export function useUpdateConversation(id: Id) {
  const { server } = useServer()
  const client = useQueryClient()
  const { sendWebsocketMessage } = useChatWebsocketContext()

  return useMutation({
    mutationFn: updateConversation,
    onSuccess: async updatedConversation => {
      const recentMessagesMap = getRecentMessagesFromCache(client)
      const updatedConversationWithStatus = {
        ...updatedConversation,
        status: {
          ...updatedConversation.status,
          recentMessage: recentMessagesMap[updatedConversation.id],
        },
      }
      await updateConversationsCache(client, updatedConversationWithStatus, id)
      await updateDetailsCache(client, updatedConversationWithStatus, id)
      sendWebsocketMessage({
        action: 'conversationEvent',
        conversationEvent: {
          oldConversationId: id,
          updatedConversation,
        },
      })
    },
  })

  async function updateConversation({
    changes,
    icon,
  }: Variables): Promise<Conversation> {
    return convertConversation(
      await server.put<Response.Conversation>(
        `/v2/chat/conversations/${id}`,
        await getConversationForm(changes, icon),
        'user',
        'multipart/form-data'
      )
    )
  }
}

async function getConversationForm(
  changes: Partial<Conversation> | undefined,
  icon: MediaFile | undefined
): Promise<FormData> {
  const form = new FormData()

  if (changes) {
    if (changes.name) {
      form.append('name', changes.name)
    }

    if ('description' in changes) {
      form.append('description', changes.description ?? '')
    }

    if ('descriptionHidden' in changes) {
      form.append(
        'hide_description',
        changes.descriptionHidden ? 'true' : 'false'
      )
    }

    if ('muted' in changes) {
      form.append('muted', changes.muted ? 'true' : 'false')
    }

    if ('managerReadOnly' in changes) {
      form.append(
        'manager_read_only',
        changes.managerReadOnly ? 'true' : 'false'
      )
    }

    if ('favorite' in changes) {
      form.append('favorite', changes.favorite ? 'true' : 'false')
    }

    if ('crewReadOnly' in changes) {
      form.append('crew_read_only', changes.crewReadOnly ? 'true' : 'false')
    }

    if ('birthdayWorkAnniversaryAndNewHireRemindersEnabled' in changes) {
      form.append(
        'birthday_and_work_anniversary_reminders_enabled',
        changes.birthdayWorkAnniversaryAndNewHireRemindersEnabled
          ? 'true'
          : 'false'
      )
    }
  }

  if (icon) {
    const file: AttachmentFileBase = {
      name: 'icon',
      uri: icon.uri,
      mimeType: icon.mimeType,
    }
    const formFieldName = 'icon'
    await appendFileToFormData(form, formFieldName, file)
  }

  return form
}
